import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Animal, MatchResult } from '@rehome/shared';
import { useShelterAnimals, searchAnimals, generateMatchReason, isGeminiConfigured, getImageUrl } from '@rehome/shared';
import { Search, Sparkles, Loader2, ArrowRight } from 'lucide-react';

// ─── SmartMatchPage ────────────────────────────────────────────────────────
// Matching for shelter staff (plan.md 4.2.4):
//   1. SQL full-text search (Hungarian) over the shelter's OWN animals
//   2. Optional Gemini garnish: a persuasive 1-2 sentence reason per match
// The search/filtering itself is SQL — Gemini never selects the animals.
// ===========================================================================

/** Simple keyword-overlap score as a deterministic fallback ranking. */
const keywordScore = (animal: Animal, query: string): number => {
  const q = query.toLowerCase();
  const words = q.split(/\s+/).filter(w => w.length > 2);
  const haystack = [animal.name, animal.species, animal.breed || '', animal.bio || '', ...(animal.traits || [])]
    .join(' ')
    .toLowerCase();
  if (words.length === 0) return 0;
  const hits = words.filter(w => haystack.includes(w)).length;
  return hits / words.length;
};

const SmartMatchPage: React.FC = () => {
  const { shelterId } = useParams<{ shelterId: string }>();
  const { data: allShelterAnimals } = useShelterAnimals(shelterId);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MatchResult[] | null>(null);
  const [matchedAnimals, setMatchedAnimals] = useState<Map<string, Animal>>(new Map());
  const [isSearching, setIsSearching] = useState(false);

  const shelterAnimals = allShelterAnimals.filter(a => a.status === 'Available');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setIsSearching(true); setResults(null);

    // 1. SQL full-text search (Hungarian dictionary), restricted to this shelter
    let candidates: Animal[] = [];
    try {
      const ftsResults = await searchAnimals(query.trim(), 30);
      candidates = ftsResults.filter(a => a.shelterId === shelterId);
    } catch { /* fall through to client-side scoring */ }

    // 2. Fallback: rank the shelter's available animals by keyword overlap
    if (candidates.length === 0) {
      candidates = shelterAnimals
        .map(a => ({ animal: a, score: keywordScore(a, query) }))
        .filter(x => x.score > 0)
        .sort((a, b) => b.score - a.score)
        .map(x => x.animal);
    }

    const top = candidates.slice(0, 3);

    // 3. Optional Gemini garnish — reasons only, never selection
    const reasons = await Promise.all(
      top.map(a =>
        isGeminiConfigured()
          ? generateMatchReason(
              { name: a.name, species: a.species, breed: a.breed, traits: a.traits || [] },
              query.trim()
            ).catch(() => null)
          : Promise.resolve(null)
      )
    );

    const matches: MatchResult[] = top.map((a, idx) => ({
      animalId: a.id,
      score: Math.max(60, 95 - idx * 12),
      reason:
        reasons[idx] ||
        `${a.name} (${a.breed || a.species}) tulajdonságai alapján jó eséllyel illik a keresett igényekhez: ${(a.traits || []).slice(0, 3).join(', ') || 'kedves természetű'}.`,
    }));

    setMatchedAnimals(new Map(top.map(a => [a.id, a])));
    setResults(matches);
    setIsSearching(false);
  };

  const getAnimalById = (id: string) => matchedAnimals.get(id);

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-3xl font-bold text-gray-900"><span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-purple-600">Smart Match</span> Kereső</h1>
        <p className="text-gray-600 max-w-2xl mx-auto">Írd le természetes nyelven az érdeklődő igényeit — a rendszer SQL-alapú kereséssel szűri a menhely állatait, és a legjobb találatokhoz személyes indoklást ad.</p>
      </div>
      <div className="bg-white p-2 rounded-2xl shadow-lg border border-gray-100 max-w-2xl mx-auto">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Pl.: Egy nyugodt kutyát keresek idősek mellé..." className="w-full pl-12 pr-4 py-4 rounded-xl outline-none text-gray-700 placeholder-gray-400 bg-white" />
          </div>
          <button type="submit" disabled={isSearching || !query} className="bg-gradient-to-r from-teal-600 to-teal-500 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-md transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            {isSearching ? <><Loader2 className="w-5 h-5 animate-spin" /> Keresés...</> : <><Sparkles className="w-5 h-5" /> Keresés</>}
          </button>
        </form>
      </div>
      {results && (
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-gray-800 px-2">Találatok ({results.length})</h2>
          <div className="grid gap-6">
            {results.map(match => {
              const animal = getAnimalById(match.animalId);
              if (!animal) return null;
              return (
                <div key={match.animalId} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col md:flex-row gap-6 hover:border-teal-200 transition-colors">
                  <div className="w-full md:w-48 h-48 flex-shrink-0 bg-gray-100 rounded-xl overflow-hidden">
                    {getImageUrl(animal) ? (
                      <img src={getImageUrl(animal)!} alt={animal.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">Nincs kép</div>
                    )}
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="flex justify-between items-start">
                      <div><h3 className="text-2xl font-bold text-gray-900">{animal.name}</h3><p className="text-gray-500">{animal.breed || animal.species} • {animal.age != null ? `${animal.age} év` : 'ismeretlen kor'}</p></div>
                      <div className="flex flex-col items-end"><span className="text-3xl font-bold text-teal-600">{match.score}%</span><span className="text-xs text-gray-400 uppercase">Egyezés</span></div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
                      <div className="flex items-start gap-2"><Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" /><p className="text-gray-700 text-sm"><span className="font-semibold text-purple-900">Indoklás: </span>{match.reason}</p></div>
                    </div>
                    <div className="flex flex-wrap gap-2">{(animal.traits || []).map(t => <span key={t} className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">{t}</span>)}</div>
                  </div>
                  <div className="flex flex-col justify-end">
                    <a href={`/shelter/${shelterId}/animals/${animal.id}`} className="bg-gray-900 text-white p-3 rounded-xl hover:bg-gray-800 transition-colors inline-flex" title="Állat profil megnyitása"><ArrowRight className="w-5 h-5" /></a>
                  </div>
                </div>
              );
            })}
          </div>
          {results.length === 0 && <div className="text-center py-12 text-gray-500 bg-white rounded-xl">Nem találtunk megfelelő egyezést a menhely elérhető állatai között.</div>}
        </div>
      )}
    </div>
  );
};

export default SmartMatchPage;
